import * as React from 'react';
import CheckBox from './CheckBox';
import validator, {
  Rule,
  RulesMessages,
  ValidationResponse
} from '../utils/validator';
import '../css/form-input-core.sass';
import '../css/form-group-error.sass';
import '../css/checkbox-group.sass';
import Card from './Card';
import { HatType } from './Hat';

export interface CheckBoxOption {
  value: string;
  label: string;
  description?: React.ReactNode;
  checked?: boolean;
  focus?: boolean;
  hideCheck?: boolean;
}

interface CheckBoxGroupProps {
  name: string;
  label: string;
  id: string;
  options: CheckBoxOption[];
  required?: boolean;
  focus?: boolean;
  hasError?: boolean;
  labelOnBottom?: boolean;
  errors?: any;
  validationRules?: Rule[];
  rulesMessages?: RulesMessages;
  onChange?: Function;
  onComplete?: Function;
  onError?: Function;
  onBlur?: Function;
  theme?: 'night' | 'day';
  grouped?: boolean;
  title?: React.ReactNode;
  hat?: HatType[];
  disabled?: boolean;
}

interface CheckBoxGroupState {
  options: CheckBoxOption[];
  validationResponse?: ValidationResponse;
}

export default function CheckBoxGroup(props: CheckBoxGroupProps) {
  const [state, setState] = React.useState<CheckBoxGroupState>({
    options: props.options
  });
  const componentState = { state, setState };
  const { theme = 'day' } = props;

  React.useEffect(() => {
    if (state.options !== props.options) {
      setState({
        ...state,
        options: props.options
      });
    }
  }, [props.options]);

  const componentPropsRules: Rule[] = [];
  if (props.validationRules) {
    componentPropsRules.push(...props.validationRules);
  }
  if (
    props.required &&
    !componentPropsRules.some(rule => rule.ruleName === 'empty')
  ) {
    componentPropsRules.push({
      ruleName: 'checkForTrue',
      message: props.rulesMessages && props.rulesMessages.empty,
      defaultMessage: `Please select at least one option for ${props.label}.`
    });
  }

  const componentValidator = {
    validationRules: [
      {
        fieldName: 'options',
        fieldValue: state.options.some((opt: CheckBoxOption) => opt.checked),
        rules: componentPropsRules
      }
    ]
  };

  let checkBoxGroup: any = [];
  state.options.forEach((option, index) => {
    checkBoxGroup.push(
      <CheckBox
        onBlur={(e: any) => {
          if (
            !e.relatedTarget ||
            e.currentTarget.getAttribute('name') !==
              e.relatedTarget.getAttribute('name')
          ) {
            if (
              validator.validateComponent(componentState, componentValidator)
            ) {
              if (props.onComplete !== undefined) {
                props.onComplete(state.options);
              }
            } else if (props.onError !== undefined) {
              props.onError();
            }
            if (props.onBlur !== undefined) {
              props.onBlur(e);
            }
          }
        }}
        onChange={(e: any) => {
          const options = [...state.options];
          const currentOption = { ...options[index] };
          currentOption.checked = e.target.checked;
          options[index] = currentOption;

          if (props.onChange !== undefined) {
            props.onChange({
              ...e,
              target: { ...e.target, value: options }
            });
          }
          setState({ ...state, options });
        }}
        checked={option.checked}
        hideCheck={option.hideCheck}
        label={option.label}
        id={`${props.id}-${index}`}
        name={props.name}
        value={option.value}
        key={`${props.id}-${option.value}`}
        focus={props.focus}
        labelOnBottom={props.labelOnBottom}
        description={option.description}
        theme={theme}
        disabled={props.disabled}
      />
    );
  });

  checkBoxGroup = props.grouped ? (
    <div className="grouped">
      <Card hat={props.hat} theme={theme}>
        {props.title && <h3> {props.title} </h3>}
        {checkBoxGroup}
      </Card>
    </div>
  ) : (
    checkBoxGroup
  );

  return (
    <>
      {(!validator.isAllValid(componentState) &&
        validator.getErrors(componentState)) ||
      (props.hasError && props.errors) ? (
        <div className="group-error">
          <div className="contextual-icon"></div>
          {(!validator.isAllValid(componentState) &&
            validator.getErrors(componentState)) ||
            (props.hasError && props.errors)}
          <div className="error-band"></div>
          {checkBoxGroup}
        </div>
      ) : (
        checkBoxGroup
      )}
    </>
  );
}
